/**
 * Session Engine v3 — Scenario planning across cut lines (SPEC-02).
 * A movable cut line splits ranked items into selected / below-the-line.
 * Exclusivity rules are evaluated live.
 */

import { useMemo, useState } from 'react';
import { AlertTriangle, Ban, CheckCircle2, Minus, Plus, Save, Trash2 } from 'lucide-react';
import { evaluateExclusivity } from '../engineLogic';
import { useDerivedExclusivity } from '../useExclusivity';
import { useSessionEngineStore } from '../store';
import { ItemRelationshipFlags } from '@/components/prioritization/ItemRelationshipFlags';

interface ScenarioBoardProps {
  rankedItemIds?: string[];
  variant?: 'full' | 'slim';
}

export function ScenarioBoard({ rankedItemIds, variant = 'full' }: ScenarioBoardProps) {
  const event = useSessionEngineStore((s) => s.event);
  const addScenario = useSessionEngineStore((s) => s.addScenario);
  const removeScenario = useSessionEngineStore((s) => s.removeScenario);
  const setActiveScenario = useSessionEngineStore((s) => s.setActiveScenario);
  const acceptCutLineToPool = useSessionEngineStore((s) => s.acceptCutLineToPool);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [acceptOk, setAcceptOk] = useState<string | null>(null);

  const ranking = rankedItemIds ?? event?.ranking ?? [];
  const [cutLine, setCutLine] = useState(() => Math.min(3, ranking.length));
  const [notes, setNotes] = useState('');
  const [compareA, setCompareA] = useState<string>('');
  const [compareB, setCompareB] = useState<string>('');

  const itemsById = useMemo(() => {
    const m = new Map<string, string>();
    event?.items.forEach((i) => m.set(i.id, i.title));
    return m;
  }, [event?.items]);

  const exclusivity = useDerivedExclusivity(ranking);

  if (!event) return null;
  if (ranking.length === 0) {
    return (
      <p className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] px-4 py-6 text-center text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)]">
        {rankedItemIds ? 'Add items first, then drag the cut line to explore scenarios.' : 'Produce a ranking first, then drag the cut line to explore scenarios.'}
      </p>
    );
  }

  const clamped = Math.max(0, Math.min(cutLine, ranking.length));
  const selectedIds = ranking.slice(0, clamped);
  const findings = evaluateExclusivity(exclusivity, ranking, selectedIds);
  // Accept rewrites the pool to above-cut only — block only if conflicts remain above.
  const acceptFindings = evaluateExclusivity(exclusivity, selectedIds, selectedIds);
  const poolLocked = event.poolState === 'locked';
  const savedScenarios = event.scenarios;

  function scenarioSelectedIds(id: string): string[] {
    const scenario = savedScenarios.find((sc) => sc.id === id);
    if (!scenario) return [];
    return scenario.selectedItemIds.length > 0 ? scenario.selectedItemIds : ranking.slice(0, scenario.cutLine);
  }

  const comparison = compareA && compareB && compareA !== compareB
    ? {
        above: scenarioSelectedIds(compareB).filter((id) => !scenarioSelectedIds(compareA).includes(id)),
        below: scenarioSelectedIds(compareA).filter((id) => !scenarioSelectedIds(compareB).includes(id)),
      }
    : null;

  return (
    <div className="space-y-4">
      {/* Cut line controls */}
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">Cut line</span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setCutLine((c) => Math.max(0, c - 1))}
            className="rounded border border-[var(--rule)] dark:border-[var(--rule-bright)] p-1 text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)]"
            aria-label="Move cut line up"
          >
            <Minus size={14} />
          </button>
          <input
            type="range"
            min={0}
            max={ranking.length}
            value={clamped}
            onChange={(e) => setCutLine(Number(e.target.value))}
            className="w-48 accent-slate-blue-500"
          />
          <button
            onClick={() => setCutLine((c) => Math.min(ranking.length, c + 1))}
            className="rounded border border-[var(--rule)] dark:border-[var(--rule-bright)] p-1 text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)]"
            aria-label="Move cut line down"
          >
            <Plus size={14} />
          </button>
        </div>
        <span className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          {clamped} above · {ranking.length - clamped} below
        </span>
        <input
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Scenario notes (optional)"
          className="min-w-0 flex-1 rounded border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] px-3 py-1.5 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder:text-[var(--text-ter)] dark:placeholder:text-[var(--text-ter)] focus:border-slate-blue-500 focus:outline-none"
        />
        <button
          type="button"
          onClick={() => {
            addScenario(`Cut at ${clamped}`, clamped, selectedIds, notes.trim() || undefined);
            setNotes('');
            setAcceptError(null);
            setAcceptOk(null);
          }}
          className="flex items-center gap-1.5 border border-[var(--rule)] dark:border-[var(--rule-bright)] px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)]"
        >
          <Save size={14} /> Save scenario
        </button>
        <button
          type="button"
          disabled={poolLocked || clamped === 0 || acceptFindings.blocks.length > 0}
          title={
            poolLocked
              ? 'Pool is already locked'
              : acceptFindings.blocks.length > 0
                ? 'Cannot-coexist items are both above the cut — move one below or remove it'
                : clamped === 0
                  ? 'Move the cut line up to keep at least one item'
                  : 'Rewrite the item pool to only items above the cut line'
          }
          onClick={() => {
            const below = ranking.length - clamped;
            if (
              below > 0 &&
              !window.confirm(
                `Keep ${clamped} item${clamped === 1 ? '' : 's'} above the cut line as the item pool?\n\n${below} item${below === 1 ? '' : 's'} below the line will be removed from this event’s pool.`,
              )
            ) {
              return;
            }
            const result = acceptCutLineToPool(ranking, clamped, notes.trim() || undefined);
            if (!result.ok) {
              setAcceptOk(null);
              setAcceptError(result.error ?? 'Could not accept cut line');
              return;
            }
            setAcceptError(null);
            setAcceptOk(
              `Pool updated: kept ${result.keptCount ?? clamped}` +
                (result.droppedCount ? `, dropped ${result.droppedCount}` : '') +
                '. Pool locked.',
            );
            setNotes('');
          }}
          className="ml-auto flex items-center gap-1.5 bg-alpine-green-700 px-3 py-1.5 text-sm font-semibold text-white hover:bg-alpine-green-600 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <CheckCircle2 size={14} /> Accept cut → set pool
        </button>
      </div>

      {acceptError && (
        <p className="border border-luminous-coral-200 dark:border-luminous-coral-800 bg-luminous-coral-50 dark:bg-luminous-coral-900/20 px-3 py-2 text-sm text-luminous-coral-800 dark:text-luminous-coral-300">
          {acceptError}
        </p>
      )}
      {acceptOk && (
        <p className="border border-alpine-green-200 dark:border-alpine-green-800 bg-alpine-green-50 dark:bg-alpine-green-900/20 px-3 py-2 text-sm text-alpine-green-800 dark:text-alpine-green-300">
          {acceptOk}
        </p>
      )}

      {/* Exclusivity violations */}
      {findings.blocks.map((b) => (
        <div key={b.groupId} className="flex items-start gap-2 bg-luminous-coral-100 dark:bg-luminous-coral-900/20 border border-luminous-coral-200 dark:border-luminous-coral-800/50 px-3 py-2 text-sm text-luminous-coral-800 dark:text-luminous-coral-300">
          <Ban size={15} className="mt-0.5 shrink-0" />
          <span>
            <strong>Cannot coexist:</strong> {b.itemIds.map((id) => itemsById.get(id) ?? id).join(' + ')} should not be in the same pool.
          </span>
        </div>
      ))}
      {findings.warnings.map((w) => (
        <div key={w.groupId} className="flex items-start gap-2 bg-electric-mustard-100 dark:bg-electric-mustard-900/20 border border-electric-mustard-200 dark:border-electric-mustard-800/50 px-3 py-2 text-sm text-electric-mustard-800 dark:text-electric-mustard-300">
          <AlertTriangle size={15} className="mt-0.5 shrink-0" />
          <span>
            <strong>Competing above the line:</strong> {w.itemIds.map((id) => itemsById.get(id) ?? id).join(' + ')} — at most one should win.
          </span>
        </div>
      ))}

      {variant === 'full' && exclusivity.length === 0 && (
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          Exclusivity (competing / cannot-coexist) is read from item relationships — author it on an item's detail page to see it here.
        </p>
      )}

      {/* Ranked list with cut line divider */}
      <ol className="overflow-hidden border border-[var(--rule)] dark:border-[var(--rule-bright)] divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
        {ranking.map((id, idx) => {
          const isAbove = idx < clamped;
          const isCutBefore = idx === clamped;
          return (
            <li key={id}>
              {isCutBefore && clamped < ranking.length && (
                <div className="flex items-center gap-3 px-3 py-1 select-none bg-[var(--hover-tint)] dark:bg-[var(--surface-raised)]">
                  <div className="flex-1 border-t border-dashed border-[var(--rule)] dark:border-[var(--rule-bright)]" />
                  <span className="shrink-0 rounded-full border border-[var(--rule)] dark:border-[var(--rule-bright)] px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                    cut · {clamped} selected
                  </span>
                  <div className="flex-1 border-t border-dashed border-[var(--rule)] dark:border-[var(--rule-bright)]" />
                </div>
              )}
              <div className="flex items-center gap-3 px-3 py-2 text-sm">
                <span className="w-6 shrink-0 text-right tabular-nums text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">{idx + 1}</span>
                <span className={isAbove ? 'flex-1 text-[var(--text-pri)] dark:text-[var(--text-pri)]' : 'flex-1 text-[var(--text-ter)] dark:text-[var(--text-ter)]'}>
                  {itemsById.get(id) ?? id}
                </span>
                <ItemRelationshipFlags itemId={id} />
                {isAbove && (
                  <span className="text-[10px] uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">in</span>
                )}
              </div>
            </li>
          );
        })}
        {clamped === ranking.length && (
          <li>
            <div className="flex items-center gap-3 px-3 py-1 bg-[var(--hover-tint)] dark:bg-[var(--surface-raised)]">
              <div className="flex-1 border-t border-dashed border-[var(--rule)] dark:border-[var(--rule-bright)]" />
              <span className="shrink-0 rounded-full border border-[var(--rule)] dark:border-[var(--rule-bright)] px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                cut · all {clamped} selected
              </span>
              <div className="flex-1 border-t border-dashed border-[var(--rule)] dark:border-[var(--rule-bright)]" />
            </div>
          </li>
        )}
      </ol>

      {/* Saved scenarios */}
      {savedScenarios.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">Saved scenarios</h4>
          <div className="flex flex-wrap gap-2">
            {savedScenarios.map((sc) => (
              <div
                key={sc.id}
                className={[
                  'flex items-center gap-2  border px-3 py-1.5 text-sm',
                  event.activeScenarioId === sc.id
                    ? 'border-slate-blue-600/60 bg-slate-blue-100 dark:bg-slate-blue-900/20 text-[var(--text-pri)] dark:text-[var(--text-pri)]'
                    : 'border-[var(--rule)] dark:border-[var(--rule-bright)] text-[var(--text-sec)] dark:text-[var(--text-sec)]',
                ].join(' ')}
              >
                <button
                  onClick={() => {
                    setCutLine(sc.cutLine);
                    setActiveScenario(sc.id);
                  }}
                  className="font-medium"
                >
                  {sc.name}
                </button>
                <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">{sc.cutLine} above</span>
                {sc.notes && <span className="max-w-40 truncate text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">{sc.notes}</span>}
                <button
                  onClick={() => removeScenario(sc.id)}
                  className="text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-luminous-coral-400"
                  aria-label="Remove scenario"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
          {savedScenarios.length >= 2 && (
            <div className="border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--surface)] dark:bg-[var(--surface)] p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">Compare</span>
                <select
                  value={compareA}
                  onChange={(e) => setCompareA(e.target.value)}
                  className="rounded border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] px-2 py-1 text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)]"
                >
                  <option value="">Scenario A</option>
                  {savedScenarios.map((sc) => <option key={sc.id} value={sc.id}>{sc.name}</option>)}
                </select>
                <select
                  value={compareB}
                  onChange={(e) => setCompareB(e.target.value)}
                  className="rounded border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] px-2 py-1 text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)]"
                >
                  <option value="">Scenario B</option>
                  {savedScenarios.map((sc) => <option key={sc.id} value={sc.id}>{sc.name}</option>)}
                </select>
              </div>
              {comparison && (
                <div className="mt-3 grid gap-2 text-xs sm:grid-cols-2">
                  <div>
                    <p className="mb-1 font-medium text-alpine-green-700 dark:text-alpine-green-300">Moves above in B</p>
                    <p className="text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                      {comparison.above.length > 0 ? comparison.above.map((id) => itemsById.get(id) ?? id).join(', ') : 'No change'}
                    </p>
                  </div>
                  <div>
                    <p className="mb-1 font-medium text-luminous-coral-700 dark:text-luminous-coral-300">Moves below in B</p>
                    <p className="text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                      {comparison.below.length > 0 ? comparison.below.map((id) => itemsById.get(id) ?? id).join(', ') : 'No change'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
